import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  LogOut,
  Search,
  Mail,
  Phone,
  Trash2,
  RefreshCw,
  Inbox,
  CheckCircle2,
  Clock as ClockIcon
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from "@/components/ui/alert-dialog";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { COMPANY } from "@/data/mock";

const STATUSES = [
  { value: "new", label: "New", color: "bg-[#C05640]/15 text-[#A64531]" },
  { value: "contacted", label: "Contacted", color: "bg-amber-100 text-amber-800" },
  { value: "quoted", label: "Quoted", color: "bg-blue-100 text-blue-800" },
  { value: "won", label: "Won", color: "bg-emerald-100 text-emerald-800" },
  { value: "lost", label: "Lost", color: "bg-zinc-100 text-zinc-700" }
];

function statusBadge(s) {
  const found = STATUSES.find((x) => x.value === s) || STATUSES[0];
  return (
    <span
      className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${found.color}`}
    >
      {found.label}
    </span>
  );
}

function formatDate(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  } catch {
    return iso;
  }
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const [quotes, setQuotes] = useState([]);
  const [stats, setStats] = useState({ total: 0, new: 0, contacted: 0, won: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [q, s] = await Promise.all([
        api.get("/admin/quotes"),
        api.get("/admin/stats")
      ]);
      setQuotes(q.data);
      setStats(s.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    return quotes.filter((q) => {
      if (statusFilter !== "all" && q.status !== statusFilter) return false;
      if (!search) return true;
      const blob = `${q.name} ${q.email} ${q.phone} ${q.service} ${q.message}`.toLowerCase();
      return blob.includes(search.toLowerCase());
    });
  }, [quotes, search, statusFilter]);

  const updateStatus = async (id, status) => {
    try {
      const { data } = await api.patch(`/admin/quotes/${id}`, { status });
      setQuotes((qs) => qs.map((q) => (q.id === id ? data : q)));
      toast.success("Status updated");
      // refresh stats counts
      api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Update failed");
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/quotes/${id}`);
      setQuotes((qs) => qs.filter((q) => q.id !== id));
      toast.success("Deleted");
      api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed");
    } finally {
      setConfirmDelete(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFCFB]" data-testid="admin-dashboard-page">
      {/* top bar */}
      <header className="border-b border-brand-line bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="admin-home-link">
            <img
              src={COMPANY.logo}
              alt="NBB logo"
              className="w-9 h-9 rounded-full object-cover ring-1 ring-brand-line"
            />
            <div className="font-display leading-none">
              <div className="text-sm font-semibold">Admin</div>
              <div className="text-[10px] tracking-[0.2em] uppercase text-brand-inkSoft">
                Neighbours Brick &amp; Build
              </div>
            </div>
          </Link>
          <div className="flex items-center gap-3">
            {user && (
              <span className="hidden sm:inline text-sm text-brand-inkSoft" data-testid="admin-user-email">
                {user.email}
              </span>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={logout}
              data-testid="admin-logout"
              className="rounded-full"
            >
              <LogOut className="w-4 h-4 mr-1.5" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-10 py-10 space-y-8">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="font-display text-3xl md:text-4xl font-medium tracking-tight text-brand-ink">
              Quote requests
            </h1>
            <p className="text-brand-inkSoft mt-1">
              All enquiries from the website. Update status as you progress them.
            </p>
          </div>
          <Button
            variant="outline"
            onClick={load}
            disabled={loading}
            className="rounded-full self-start md:self-auto"
            data-testid="admin-refresh"
          >
            <RefreshCw className={`w-4 h-4 mr-1.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
        </div>

        {/* stat cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: "Total", value: stats.total, icon: Inbox, color: "text-brand-ink" },
            { label: "New", value: stats.new, icon: Inbox, color: "text-brand-terracotta" },
            { label: "Contacted", value: stats.contacted, icon: ClockIcon, color: "text-amber-700" },
            { label: "Won", value: stats.won, icon: CheckCircle2, color: "text-emerald-700" }
          ].map((s) => (
            <div
              key={s.label}
              data-testid={`admin-stat-${s.label}`}
              className="rounded-2xl border border-brand-line bg-white p-5"
            >
              <div className="flex items-center justify-between">
                <p className="text-xs uppercase tracking-[0.18em] text-brand-inkSoft">{s.label}</p>
                <s.icon className={`w-4 h-4 ${s.color}`} />
              </div>
              <p className="mt-3 font-display text-3xl font-semibold tracking-tight text-brand-ink">
                {s.value}
              </p>
            </div>
          ))}
        </div>

        {/* filters */}
        <div className="flex flex-col md:flex-row gap-3 md:items-center">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-inkSoft" />
            <Input
              placeholder="Search name, email, message…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="admin-search"
              className="pl-10 h-11 rounded-xl bg-white border-brand-line"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger
              data-testid="admin-status-filter"
              className="h-11 w-full md:w-48 rounded-xl bg-white border-brand-line"
            >
              <SelectValue placeholder="All statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {STATUSES.map((s) => (
                <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* table */}
        <div className="rounded-2xl border border-brand-line bg-white overflow-hidden">
          <Table data-testid="admin-quotes-table">
            <TableHeader>
              <TableRow className="bg-brand-bg">
                <TableHead className="w-[170px]">Received</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Service</TableHead>
                <TableHead>Budget</TableHead>
                <TableHead>Timeline</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-10 text-brand-inkSoft">
                    Loading…
                  </TableCell>
                </TableRow>
              )}
              {!loading && filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-10 text-brand-inkSoft" data-testid="admin-quotes-empty">
                    No quote requests yet.
                  </TableCell>
                </TableRow>
              )}
              {!loading &&
                filtered.map((q) => (
                  <TableRow
                    key={q.id}
                    data-testid={`admin-quote-row-${q.id}`}
                    className="cursor-pointer hover:bg-brand-bg"
                    onClick={() => setSelected(q)}
                  >
                    <TableCell className="text-sm text-brand-inkSoft whitespace-nowrap">
                      {formatDate(q.created_at)}
                    </TableCell>
                    <TableCell>
                      <div className="font-medium text-brand-ink">{q.name}</div>
                      <div className="text-xs text-brand-inkSoft">{q.email}</div>
                    </TableCell>
                    <TableCell className="capitalize">{q.service.replace("-", " ")}</TableCell>
                    <TableCell className="text-sm">{q.budget || "—"}</TableCell>
                    <TableCell className="text-sm">{q.timeline || "—"}</TableCell>
                    <TableCell>{statusBadge(q.status)}</TableCell>
                    <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                      <div className="inline-flex items-center gap-2">
                        <Select
                          value={q.status}
                          onValueChange={(v) => updateStatus(q.id, v)}
                        >
                          <SelectTrigger
                            data-testid={`admin-status-${q.id}`}
                            className="h-9 w-[130px] rounded-lg"
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {STATUSES.map((s) => (
                              <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Button
                          variant="ghost"
                          size="icon"
                          data-testid={`admin-delete-${q.id}`}
                          className="text-destructive hover:text-destructive"
                          onClick={() => setConfirmDelete(q)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </div>
      </main>

      {/* detail dialog */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-2xl" data-testid="admin-quote-dialog">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-2xl">
                  {selected.name}
                </DialogTitle>
                <DialogDescription>
                  Submitted {formatDate(selected.created_at)} · {statusBadge(selected.status)}
                </DialogDescription>
              </DialogHeader>
              <div className="grid sm:grid-cols-2 gap-4 mt-2 text-sm">
                <a
                  href={`mailto:${selected.email}`}
                  className="rounded-xl border border-brand-line p-4 hover:border-brand-terracotta/50"
                >
                  <p className="text-xs uppercase tracking-[0.18em] text-brand-inkSoft flex items-center gap-2">
                    <Mail className="w-3.5 h-3.5" /> Email
                  </p>
                  <p className="mt-1 text-brand-ink font-medium break-all">{selected.email}</p>
                </a>
                <a
                  href={`tel:${selected.phone}`}
                  className="rounded-xl border border-brand-line p-4 hover:border-brand-terracotta/50"
                >
                  <p className="text-xs uppercase tracking-[0.18em] text-brand-inkSoft flex items-center gap-2">
                    <Phone className="w-3.5 h-3.5" /> Phone
                  </p>
                  <p className="mt-1 text-brand-ink font-medium">{selected.phone}</p>
                </a>
                <div className="rounded-xl bg-brand-bg p-4">
                  <p className="text-xs uppercase tracking-[0.18em] text-brand-inkSoft">Service</p>
                  <p className="mt-1 capitalize text-brand-ink">{selected.service.replace("-", " ")}</p>
                </div>
                <div className="rounded-xl bg-brand-bg p-4">
                  <p className="text-xs uppercase tracking-[0.18em] text-brand-inkSoft">Budget · Timeline</p>
                  <p className="mt-1 text-brand-ink">
                    {(selected.budget || "—") + " · " + (selected.timeline || "—")}
                  </p>
                </div>
              </div>
              <div className="mt-2">
                <p className="text-xs uppercase tracking-[0.18em] text-brand-inkSoft mb-2">Message</p>
                <div className="rounded-xl bg-brand-bg p-4 text-brand-ink whitespace-pre-wrap leading-relaxed text-[15px]">
                  {selected.message}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* delete alert */}
      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent data-testid="admin-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this quote request?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes the request from the database. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="admin-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="admin-delete-confirm"
              onClick={() => confirmDelete && remove(confirmDelete.id)}
              className="bg-destructive text-white hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
